import React, { useEffect, useState, useCallback, useMemo } from "react"
import _get from "lodash/get"
import debounce from "lodash/debounce"

import Input from "@/components/core/Input/Input"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import Button from "@/components/core/Button/Button"
import Dropdown from "@/components/core/SelectDropdown/SelectDropdown"

import {
  destructureAddress,
  validateField,
  getStateCode,
  getFieldValidationMessage,
} from "@/utils/helper"
import { convertToUSPhoneNumber, validateUSPhone } from "@/js/helperV2"
import { getAddressSuggestions } from "@/components/checkout/checkoutHelperFunction"
import {
  getStateName,
  getCountryName,
} from "@/components/RegisterOnlineProduct/v1/RegisterOnlineHelper"
import {
  getAddAnotherAddressAnalytics,
  addAddAddressAnalytics,
} from "@/components/AccountSettings/v1/AddressForm/analytics"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"

import PlusIcon from "@/public/icons/plus-small.svg"
import phoneIcon from "@/public/icons/phone-icon.svg"
import PropTypes from "prop-types"
import useWindowResize from "@/hooks/useWindowResize"
import { getFilteredAddress } from "@/components/checkout/v2/utils"
import { removeExtraSpaces } from "@/utils/index"
import { CA_POSTAL_SANITIZE_REGEX } from "@/constants/regex"

export const AddressForm = ({
  i18n = {},
  country = "",
  countries = [],
  onAdd = () => {},
  onEdit = () => {},
  stateData = [],
  data: propData = {},
  onCloseRef = {},
  isDefault = false,
  genericMessages = [],
  ...props
}) => {
  const {
    globalConfig: {
      general: { siteName = "" } = {},
      regexPattern: { map: { regexForAptField = "" } = {} } = {},
    } = {},
  } = useSelector(selectGenericState)

  const [width] = useWindowResize()
  const [genericMessagesMap, setGenericMessagesMap] = useState({})
  const isMobile = width <= 639

  useEffect(() => {
    if (genericMessages?.length) {
      const genericMessagesMap = {}
      genericMessages.forEach(msg => {
        genericMessagesMap[msg.text] = msg.value || ""
      })
      setGenericMessagesMap(genericMessagesMap)
    }
  }, [genericMessages])

  const getPoBoxErrorMessage = () => {
    return (
      genericMessagesMap["CHECKOUT_PO_BOX_ERROR"] || i18n?.poBoxAddressError
    )
  }

  const isAllowedAddress = address => {
    return getFilteredAddress(removeExtraSpaces(address))
  }

  const editInitialValue = {
    firstName: {
      value: _get(propData, "firstName.value", ""),
      error: false,
      message: "",
    },
    lastName: {
      value: _get(propData, "lastName.value", ""),
      error: false,
      message: "",
    },
    address1: {
      value: _get(propData, "address1.value", ""),
      error: false,
      message: "",
    },
    address2: {
      value: _get(propData, "address2.value", ""),
      error: false,
      message: "",
    },
    city: {
      value: _get(propData, "city.value", ""),
      error: false,
      message: "",
    },
    state: {
      value: _get(propData, "state.value", ""),
      error: false,
      message: "",
    },
    zipcode: {
      value: _get(propData, "zipcode.value", ""),
      error: false,
      message: "",
    },
    label: {
      show: isDefault,
      value: _get(propData, "label.value", ""),
    },
    addresstypes: {
      value: _get(propData, "addresstypes.value", ""),
      error: false,
      message: "",
    },
    phone: {
      value: _get(propData, "phone.value", ""),
      error: false,
      message: "",
    },
  }

  const addInitialValue = [
    {
      firstName: {
        value: _get(propData, "firstName.value", ""),
        error: false,
        message: "",
      },
      lastName: {
        value: _get(propData, "lastName.value", ""),
        error: false,
        message: "",
      },
      address1: {
        value: _get(propData, "address1.value", ""),
        error: false,
        message: "",
      },
      address2: {
        value: _get(propData, "address2.value", ""),
        error: false,
        message: "",
      },
      city: {
        value: _get(propData, "city.value", ""),
        error: false,
        message: "",
      },
      state: {
        value: getStateName(
          _get(propData, "state.value", ""),
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      zipcode: {
        value: _get(propData, "zipcode.value", ""),
        error: false,
        message: "",
      },
      country: {
        value: getCountryName(
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      label: { show: false, value: _get(propData, "label.value", "") },
      addresstypes: {
        value: _get(propData, "addresstypes.value", ""),
        error: false,
        message: "",
      },
      phone: {
        value: _get(propData, "phone.value", ""),
        error: false,
        message: "",
      },
    },
  ]
  const { edit = false } = propData
  const [data, setData] = useState(edit ? editInitialValue : addInitialValue)

  const [suggestions, setSuggestions] = useState(
    edit
      ? []
      : [
          {
            value: [],
          },
        ]
  )

  const handleAddAddress = () => {
    const value = [...data]
    const list = [...suggestions]
    value.push({
      firstName: {
        value: _get(propData, "firstName.value", ""),
        error: false,
        message: "",
      },
      lastName: {
        value: _get(propData, "lastName.value", ""),
        error: false,
        message: "",
      },
      address1: {
        value: _get(propData, "address1.value", ""),
        error: false,
        message: "",
      },
      address2: {
        value: _get(propData, "address2.value", ""),
        error: false,
        message: "",
      },
      city: {
        value: _get(propData, "city.value", ""),
        error: false,
        message: "",
      },
      state: {
        value: getStateName(
          _get(propData, "state.value", ""),
          _get(propData, "country.value", country),
          countries
        ),
        error: false,
        message: "",
      },
      zipcode: {
        value: _get(propData, "zipcode.value", ""),
        error: false,
        message: "",
      },
      country: {
        value: getCountryName(
          _get(propData, "country.value", country ?? ""),
          countries
        ),
        error: false,
        message: "",
      },
      label: { show: false, value: _get(propData, "label.value", "") },
      addresstypes: {
        value: _get(propData, "addresstypes.value", ""),
        error: false,
        message: "",
      },
      phone: {
        value: _get(propData, "phone.value", ""),
        error: false,
        message: "",
      },
    })
    list.push({
      value: [],
    })
    setData(value)
    setSuggestions(list)
  }

  const validateFieldData = async (
    field,
    value,
    errorMessages,
    errorFields,
    index = null
  ) => {
    const addError = messageKey => {
      const message = messageKey || i18n[messageKey]
      errorMessages.push(message)
      errorFields.push(index !== null ? `${field}${index}` : field)
      return { error: true, message }
    }

    if (field === "zipcode") {
      if (!value) return addError(genericMessagesMap["ZIPCODE_ERROR"])

      if (value.length < 5) {
        return addError(genericMessagesMap["ZIPCODE_MIN_ERROR"])
      }
      const zipfield =
        siteName === "kohler-canada" ? "zipcode" : "zipCodeNumeric"
      const isValid = await validateField(zipfield, value)
      if (!isValid) {
        return addError(genericMessagesMap["VALID_ZIPCODE"])
      }
    }

    if (field === "phone" && !validateUSPhone(value)) {
      return addError(genericMessagesMap["VALID_US_PHONE"])
    }

    const fieldRegexMap = {
      firstName: "name",
      lastName: "name",
      city: "city",
      address1: "address",
      state: "state",
    }

    const requiredFieldMessageMap = {
      firstName: genericMessagesMap["FIRST_NAME_ERROR"],
      lastName: genericMessagesMap["LAST_NAME_ERROR"],
      city: genericMessagesMap["CITY_ERROR"],
      address1: genericMessagesMap["ADDRESS_ERROR"],
      state: genericMessagesMap["STATE_ERROR"],
    }

    const regexKey = fieldRegexMap[field]
    if (regexKey) {
      if (!value) {
        return addError(requiredFieldMessageMap[field])
      }

      if (field === "address1" && !isAllowedAddress(value)) {
        return addError(getPoBoxErrorMessage())
      }

      const isValid = await validateField(regexKey, value)
      if (!isValid) {
        return addError(getFieldValidationMessage(regexKey))
      }
    }

    return { error: false, message: "" }
  }

  const validateData = async (dataItem, index = null) => {
    const errorMessages = []
    const errorFields = []
    const fieldsToValidate = [
      "phone",
      "address1",
      "firstName",
      "lastName",
      "city",
      "state",
      "zipcode",
    ]
    const updatedData = { ...dataItem }

    for (const field of fieldsToValidate) {
      const validation = await validateFieldData(
        field,
        dataItem[field]?.value,
        errorMessages,
        errorFields,
        index
      )
      updatedData[field] = { ...dataItem[field], ...validation }
    }

    return { updatedData, errorMessages, errorFields }
  }
  const handleSubmit = async (actionLabel = "") => {
    if (edit) {
      const { updatedData, errorMessages, errorFields } = await validateData(
        data
      )
      setData(updatedData)

      if (errorFields.length > 0) {
        document.getElementById(`edit-address-${errorFields[0]}`)?.focus()
        addAddAddressAnalytics("failure", errorMessages, edit, actionLabel)
      } else {
        onEdit(data)
        addAddAddressAnalytics("success", "", edit, actionLabel)
      }
    } else {
      const allErrorMessages = []
      const allErrorFields = []
      const updatedDataArray = await Promise.all(
        data.map(async (dataItem, index) => {
          const { updatedData, errorMessages, errorFields } =
            await validateData(dataItem, index)
          allErrorMessages.push(...errorMessages)
          allErrorFields.push(...errorFields)
          return updatedData
        })
      )

      setData(updatedDataArray)

      if (allErrorFields.length > 0) {
        document.getElementById(`add-address-${allErrorFields[0]}`)?.focus()
        addAddAddressAnalytics(
          "failure",
          allErrorMessages,
          false,
          actionLabel || "save"
        )
      } else {
        addAddAddressAnalytics("success", [], false, actionLabel || "save")
        onAdd(data)
      }
    }
  }

  const handleAddChange = async (index, name, e) => {
    const value = e?.target?.value

    const updateData = updatedField => {
      if (edit) {
        setData({ ...data, ...updatedField })
      } else {
        const addArry = [...data]
        Object.assign(addArry[index], updatedField)
        setData(addArry)
      }
    }

    const handleLabelChange = () => {
      if (edit) {
        updateData({ label: { ...data?.label, show: e?.target?.checked } })
      } else {
        const addArry = [...data]
        addArry.forEach((obj, i) => {
          Object.assign(obj?.label, {
            show: index === i ? e?.target?.checked : !e?.target?.checked,
          })
        })
        setData(addArry)
      }
    }

    const handleZipcodeChange = async () => {
      const field = siteName === "kohler-canada" ? "zipcode" : "zipCodeNumeric"

      if (siteName === "kohler-canada") {
        const raw = (value || "")
          .toUpperCase()
          .replace(CA_POSTAL_SANITIZE_REGEX, "")
          .slice(0, 6)
        const formatted =
          raw.length > 3 ? `${raw.slice(0, 3)} ${raw.slice(3)}` : raw

        // allow typing first
        updateData({ [name]: { value: formatted, error: false, message: "" } })
        return
      }
      if ((await validateField(field, value)) || value === "") {
        updateData({ [name]: { value, error: false, message: "" } })
      }
    }

    const handleDefaultChange = () => {
      if (
        name === "address2" &&
        regexForAptField &&
        !value.match(new RegExp(regexForAptField))
      ) {
        return
      }

      const formattedValue =
        name === "phone" ? convertToUSPhoneNumber(value) : value

      updateData({
        [name]: { value: formattedValue, error: false, message: "" },
      })
    }

    switch (name) {
      case "label":
        handleLabelChange()
        break
      case "state":
        updateData({ [name]: { value: e, error: false, message: "" } })
        break
      case "zipcode":
        await handleZipcodeChange()
        break
      default:
        handleDefaultChange()
        break
    }
  }
  const handleRemoveAddress = index => {
    const addressArry = [...data]
    addressArry.splice(index, 1)
    setData(addressArry)
  }

  const removeAddressEventInfo = JSON.stringify({
    clickInternalLinks: "true",
    eventAction: "my account:account settings:modal:add address:remove",
    eventName: "my account:account settings:modal:add address:remove",
    eventStatus: "n/a",
    eventMsg: "n/a",
    eventType: "navigation",
    internalLinkName: "remove",
    internalLinkPosition: "my account",
    internalLinkType: "my account:addresses",
    internalLinkURL: "n/a",
    internalLinkZoneName: "my account:account settings:modal:add address",
  })

  const stateCodes = useMemo(() => {
    const countryValue = data?.[0]?.country?.value || country

    return (
      countries
        .find(country => country.name === countryValue)
        ?.states.map(state => state.code) ?? []
    )
  }, [data, country, countries])

  const handleAddressChange = (index, e) => {
    handleAddChange(index, "address1", e)
    getSuggestions(e?.target?.value, stateCodes, index)
  }

  const getSuggestions = useCallback(
    debounce(async (addr, stateCodes, index) => {
      if (addr?.length >= 3) {
        setSuggestions(prev => {
          const newSuggestions = [...prev]
          newSuggestions[index] = { value: [] }
          return newSuggestions
        })
        if (addr && (await validateField("address1", addr))) {
          const useGeoLocation = true // Assuming we always want to use geolocation for suggestions
          getAddressSuggestions(addr, useGeoLocation)
            .then((suggData = []) => {
              // filter address suggestion using authored regex (For PO Box)
              const filteredAddress = getFilteredAddress(suggData)
              const filteredAddrWithState = filteredAddress.filter(address => {
                if (stateCodes?.length) {
                  const desAddress = destructureAddress(address, siteName)
                  return stateCodes.some(stateCode =>
                    desAddress.state.includes(stateCode)
                  )
                }
                return address
              })

              setSuggestions(prev => {
                const newSuggestions = [...prev]
                newSuggestions[index] = { value: filteredAddrWithState }
                return newSuggestions
              })
            })
            .catch(error => {
              setSuggestions(prev => {
                const newSuggestions = [...prev]
                newSuggestions[index] = { value: [] }
                return newSuggestions
              })
            })
        }
      }
    }, 300),
    [siteName]
  )
  const onSuggestionSelect = (index, val) => {
    if (edit) {
      setSuggestions([])
      const addr = destructureAddress(val, siteName)

      setData({
        ...data,
        address1: { value: addr?.address, error: false, message: "" },
        city: { value: addr?.city, error: false, message: "" },
        state: {
          value: getStateCode(addr?.state, country, countries) || addr?.state,
          error: false,
          message: "",
        },
        zipcode: { value: addr?.zipcode, error: false, message: "" },
      })
    } else {
      const list = [...suggestions]
      list[index] = { ...suggestions[index], value: [] }
      setSuggestions(list)
      const addr = destructureAddress(val, siteName)
      const dataItems = [...data]
      const addressItems = {
        ...data[index],
        address1: { value: addr.address, error: false, message: "" },
        city: { value: addr.city, error: false, message: "" },
        state: {
          value: getStateCode(addr.state, country, countries) || addr?.state,
          error: false,
          message: "",
        },
        zipcode: { value: addr.zipcode, error: false, message: "" },
      }
      dataItems[index] = addressItems
      setData(dataItems)
    }
  }
  const clearDataOnClose = () => {
    setData([
      {
        firstName: {
          value: _get(propData, "firstName.value", ""),
          error: false,
          message: "",
        },
        lastName: {
          value: _get(propData, "lastName.value", ""),
          error: false,
          message: "",
        },
        address1: {
          value: _get(propData, "address1.value", ""),
          error: false,
          message: "",
        },
        address2: {
          value: _get(propData, "address2.value", ""),
          error: false,
          message: "",
        },
        city: {
          value: _get(propData, "city.value", ""),
          error: false,
          message: "",
        },
        state: {
          value: getStateName(
            _get(propData, "state.value", ""),
            _get(propData, "country.value", country ?? ""),
            countries
          ),
          error: false,
          message: "",
        },
        zipcode: {
          value: _get(propData, "zipcode.value", ""),
          error: false,
          message: "",
        },
        country: {
          value: getCountryName(
            _get(propData, "country.value", country ?? ""),
            countries
          ),
          error: false,
          message: "",
        },
        label: { show: false, value: _get(propData, "label.value", "") },
        addresstypes: {
          value: _get(propData, "addresstypes.value", ""),
          error: false,
          message: "",
        },
        phone: {
          value: _get(propData, "phone.value", ""),
          error: false,
          message: "",
        },
      },
    ])
  }
  onCloseRef.clearDataOnClose = clearDataOnClose

  const onBlurInput = async (index, name, value) => {
    const dataCopy = Array.isArray(data) ? [...data] : { ...data }
    const setError = (error, message) => {
      if (Array.isArray(dataCopy)) {
        dataCopy[index][name] = { ...dataCopy[index][name], error, message }
      } else {
        dataCopy[name] = { ...dataCopy[name], error, message }
      }
      setData(dataCopy)
    }

    const validateZipcode = async () => {
      if (!value) {
        setError(
          true,
          genericMessagesMap["ZIPCODE_ERROR"] || i18n?.zipcodeError
        )
      } else if (value.length < 5) {
        setError(
          true,
          genericMessagesMap["ZIPCODE_MIN_ERROR"] || i18n?.zipCodeMinError
        )
      } else {
        const field =
          siteName === "kohler-canada" ? "zipcode" : "zipCodeNumeric"
        const isValid = await validateField(field, value)
        setError(
          !isValid,
          isValid
            ? ""
            : genericMessagesMap["VALID_ZIPCODE"] || i18n?.zipcodeError
        )
      }
    }

    const validateNameInput = async (field, requiredMessage) => {
      if (value) {
        const isValid = await validateField(field, value)
        if (isValid) {
          setError(false, "")
          return
        }
        setError(true, getFieldValidationMessage(field, genericMessagesMap))
        return
      }

      setError(true, requiredMessage)
    }

    const validations = {
      phone: () =>
        setError(
          !validateUSPhone(value),
          validateUSPhone(value)
            ? ""
            : genericMessagesMap["VALID_US_PHONE"] || i18n?.validUSPhone
        ),
      address1: () =>
        validateNameInput(
          "address",
          genericMessagesMap["ADDRESS_ERROR"] || i18n?.addressError
        ),
      city: () =>
        validateNameInput(
          "city",
          genericMessagesMap["CITY_ERROR"] || i18n?.cityError
        ),
      state: () =>
        setError(
          !value,
          !value ? genericMessagesMap["STATE_ERROR"] || i18n?.stateError : ""
        ),
      zipcode: validateZipcode,
      firstName: () =>
        validateNameInput(
          "name",
          genericMessagesMap["FIRST_NAME_ERROR"] || i18n?.firstNameError
        ),
      lastName: () =>
        validateNameInput(
          "name",
          genericMessagesMap["LAST_NAME_ERROR"] || i18n?.lastNameError
        ),
    }

    // Code to check for the PO box address
    if (name === "address1" && value && !isAllowedAddress(value)) {
      setError(true, getPoBoxErrorMessage())
      return
    }

    if (validations[name]) {
      await validations[name]()
    }
  }

  const finalForm = Array.isArray(data) ? data : [data]

  const { save = "" } = i18n
  return (
    <div className="address-form container px-0 mx-0">
      <span className="address-form__required-fields">
        {`${i18n?.requiredFields}`}
      </span>
      {finalForm
        ? finalForm?.map((addData, index) => (
            <div key={index}>
              {index > 0 ? (
                <div className="address-form__addressTitle">
                  <span>
                    {i18n?.address} {index + 1}
                  </span>
                  <span
                    data-gbh-data-layer={removeAddressEventInfo}
                    onClick={() => handleRemoveAddress(index)}
                    className="address-form__removeaddress gbh-data-layer"
                    role="button"
                    tabIndex="0"
                  >
                    {i18n?.remove}
                  </span>
                </div>
              ) : null}
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-firstName`
                        : `add-address-firstName${index}`
                    }
                    type="char"
                    value={addData?.firstName?.value}
                    label={`${i18n.firstName}*`}
                    maxLength={40}
                    placeholder={i18n?.firstName}
                    showError={addData?.firstName?.error}
                    errorMessage={addData?.firstName?.message}
                    onChange={e => handleAddChange(index, "firstName", e)}
                    onBlur={e =>
                      onBlurInput(index, "firstName", e?.target?.value)
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-lastName`
                        : `add-address-lastName${index}`
                    }
                    type="text"
                    value={addData?.lastName?.value}
                    label={`${i18n.lastName}*`}
                    maxLength={40}
                    placeholder={i18n?.lastName}
                    showError={addData?.lastName?.error}
                    errorMessage={addData?.lastName?.message}
                    onChange={e => handleAddChange(index, "lastName", e)}
                    onBlur={e =>
                      onBlurInput(index, "lastName", e?.target?.value)
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-address1`
                        : `add-address-address1${index}`
                    }
                    type="text"
                    value={addData?.address1?.value}
                    label={`${
                      addData?.address1?.value
                        ? isMobile
                          ? genericMessagesMap["ADDRESS_LABEL_MOBILE_TEXT"]
                          : genericMessagesMap["ADDRESS_LABEL_TEXT"]
                        : `${i18n?.address}`
                    }*`}
                    maxLength={30}
                    placeholder={`${i18n?.address}*`}
                    onChange={e => handleAddressChange(index, e)}
                    onBlur={e =>
                      onBlurInput(index, "address1", e?.target?.value)
                    }
                    showError={addData?.address1?.error}
                    errorMessage={addData?.address1?.message}
                    suggestions={suggestions[index]?.value}
                    onSelect={val => onSuggestionSelect(index, val)}
                    autoComplete={true}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit
                        ? `edit-address-floor`
                        : `add-address-address2${index}`
                    }
                    type="text"
                    value={addData?.address2?.value}
                    placeholder={i18n?.aptFloorSuite}
                    label={i18n?.aptFloorSuite}
                    showError={addData?.address2?.error}
                    errorMessage={addData?.address2?.message}
                    onChange={e => handleAddChange(index, "address2", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={edit ? `edit-address-city` : `add-address-city${index}`}
                    type="text"
                    value={addData?.city?.value}
                    label={`${i18n?.city}*`}
                    showError={addData?.city?.error}
                    errorMessage={addData?.city?.message}
                    onChange={e => handleAddChange(index, "city", e)}
                    onBlur={e => onBlurInput(index, "city", e?.target?.value)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="left-half-field col-6">
                  <Dropdown
                    id={
                      edit ? `edit-address-state` : `add-address-state${index}`
                    }
                    data={stateData ?? []}
                    label={`${i18n?.state}*`}
                    value={addData?.state?.value}
                    showError={addData?.state?.error}
                    errorMessage={addData?.state?.message}
                    onChange={val => handleAddChange(index, "state", val)}
                    onClose={() =>
                      onBlurInput(index, "state", addData?.state?.value)
                    }
                    buttonId="add-address-state"
                    ariaStateText={true}
                  />
                </div>
                <div className="right-half-field col-6">
                  <Input
                    id={
                      edit
                        ? `edit-address-zipcode`
                        : `add-address-zipcode${index}`
                    }
                    type="text"
                    value={addData?.zipcode?.value}
                    maxLength={7}
                    autoComplete={true}
                    placeholder={`${i18n?.zipcode}*`}
                    showError={addData?.zipcode?.error}
                    errorMessage={addData?.zipcode?.message}
                    label={`${i18n?.zipcode}*`}
                    onChange={e => handleAddChange(index, "zipcode", e)}
                    onBlur={e =>
                      onBlurInput(index, "zipcode", e?.target?.value)
                    }
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit ? `edit-address-phone` : `add-address-phone${index}`
                    }
                    type="text"
                    value={addData?.phone?.value}
                    label={`${i18n?.phone}*`}
                    prefix={
                      <img
                        src={phoneIcon?.src}
                        alt="phone-icon"
                        aria-hidden="true"
                      />
                    }
                    placeholder={`${i18n?.phone}*`}
                    onChange={e => handleAddChange(index, "phone", e)}
                    showError={addData?.phone?.error}
                    errorMessage={addData?.phone?.message}
                    onBlur={e => onBlurInput(index, "phone", e?.target?.value)}
                    haspopup={false}
                    maxLength={14}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Input
                    id={
                      edit ? `edit-address-label` : `add-address-label${index}`
                    }
                    type="text"
                    value={addData?.addresstypes?.value}
                    label={`${i18n?.setDefaultLabel}`}
                    showError={addData?.addresstypes?.error}
                    errorMessage={addData?.addresstypes?.message}
                    maxLength={25}
                    onChange={e => handleAddChange(index, "addresstypes", e)}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <Checkbox
                    value={i18n?.primaryAddress}
                    onChange={e => handleAddChange(index, "label", e)}
                    checked={addData?.label?.show}
                  />
                </div>
              </div>
            </div>
          ))
        : null}
      {!edit && (
        <div
          className="address-form__addaddress gbh-data-layer"
          data-gbh-data-layer={getAddAnotherAddressAnalytics()}
        >
          <span role="button" tabIndex="0" onClick={() => handleAddAddress()}>
            <img src={PlusIcon?.src} alt="" />
            {i18n?.addAnotherAddress}
          </span>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <Button
            type="black"
            label={save}
            size="large"
            flexible={true}
            onClick={() => {
              handleSubmit("save")
            }}
            role="button"
          />
        </div>
      </div>
    </div>
  )
}

AddressForm.propTypes = {
  i18n: PropTypes.object,
  country: PropTypes.string,
  countries: PropTypes.array,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  stateData: PropTypes.array,
  data: PropTypes.object,
  onCloseRef: PropTypes.object,
  isDefault: PropTypes.bool,
  genericMessages: PropTypes.array,
}

export default AddressForm
